import { Input } from "antd";
import { useState } from "react";
import styled from "styled-components";
import { LabelledElement } from "pages/EditAdBlock/components/Fields";

export const DecimalInput = ({
    defaultValue = "",
    onChange,
    label,
    prefix = "$",
    labeled = false,
    ...props }) => {
    const [value, setValue] = useState(defaultValue);

    // Helper function to format and set value
    const formatAndSetValue = (newValues) => {
        const formattedValue = `${newValues[0]}.${newValues[1]}${newValues[2]}`;
        setValue(formattedValue);
        onChange?.(formattedValue);
    };

    // Helper function to set caret position safely
    const setCaretPosition = (target, position) => {
        setTimeout(() => {
            if (target) {
                target.setSelectionRange(position, position);
            }
        }, 0);
    };

    // Helper function to parse input into parts
    const parseInput = (inputValue) => {
        const parts = inputValue.split(".");
        let intPart = parts[0] || "0";
        let decPart = (parts[1] || "").padEnd(2, "0").slice(0, 2);

        return [intPart, decPart[0], decPart[1]];
    };

    const handleChange = (e) => {
        e.preventDefault();
        // Capture event properties synchronously to avoid synthetic event pooling issues
        const prevValue = value;
        const rawValue = e.target.value;
        const caretPosition = e.target.selectionStart;
        const inputType = e.nativeEvent?.inputType;
        const inputData = e.nativeEvent?.data;
        const target = e.target;

        if (inputType === "deleteContentForward" && caretPosition === prevValue.indexOf(".")) {
            setCaretPosition(target, caretPosition + 1);
            return;
        }

        const cleaned = rawValue.replace(/^\$/, "");
        const dotTwoDigits = cleaned.match(/^\.(\d)(\d)$/);
        if (dotTwoDigits) {
            formatAndSetValue(["0", dotTwoDigits[1], dotTwoDigits[2]]);
            setCaretPosition(target, 1);
            return;
        }

        // If input is not a number or dot, ignore
        if (inputType !== "deleteContentBackward") {
            if (/[^0-9.]/.test(inputData)) {
                setCaretPosition(target, caretPosition);
                return;
            }
        }

        // Handle backspace at decimal point - prevent deletion of decimal
        if (inputType === "deleteContentBackward" && caretPosition === prevValue.indexOf(".")) {
            setCaretPosition(target, prevValue.indexOf("."));
            return;
        }

        // Handle decimal point input
        if (inputData === ".") {
            const newValues = parseInput(rawValue);
            formatAndSetValue(newValues);

            const decimalIndex = rawValue.indexOf(".");
            if (decimalIndex !== -1) {
                setCaretPosition(target, decimalIndex + 1);
            }
            return;
        }

        if (caretPosition <= rawValue.indexOf(".") && prevValue === '0.00') {
            // inputData is single digit before decimal
            if (/^\d$/.test(inputData)) {
                const newValues = parseInput(inputData);
                formatAndSetValue(newValues);
                setCaretPosition(target, 1);
                return;
            }
        }

        // Handle single digit input (add .00 formatting)
        if (caretPosition === 1 && rawValue.length === 1 && /^\d$/.test(rawValue)) {
            const newValues = [rawValue, "0", "0"];
            formatAndSetValue(newValues);
            setCaretPosition(target, 1);
            return;
        }

        // Handle general input
        const newValues = parseInput(rawValue);
        formatAndSetValue(newValues);

        // Adjust caret position to account for auto-added decimal point
        let newCaretPosition = caretPosition;
        if (!rawValue.includes(".") && caretPosition > newValues[0].length) {
            newCaretPosition += 1;
        }
        setCaretPosition(target, newCaretPosition);
    };

    return (
        <Container>
            {labeled &&
                <LabelledElement label={label} type="text" >
                    <Input
                        value={value}
                        onChange={handleChange}
                        prefix={prefix}
                        {...props}
                    />
                </LabelledElement>
            }
            {!labeled &&
                <>
                    <Label htmlFor={label}>{label}</Label>
                    <Input
                        value={value}
                        onChange={handleChange}
                        prefix={prefix}
                        {...props}
                    />
                </>
            }
        </Container>
    );
};

const Container = styled.div`
  position: relative;
`;

const Label = styled.label`
  font-size: ${(props) => props.theme.font.size.s};
  color: ${(props) => props.theme.palette.secondary};
  display: flex;
  margin-bottom: 0;
`;
